/**
 * useAudioCache — Hook to interact with the Service Worker audio cache.
 * 
 * Provides:
 * - cacheAudioFiles(urls) — pre-cache audio files for offline playback
 * - clearCache() — clear all cached audio
 * - getCachedUrls() — get list of currently cached URLs
 * - isCaching — loading state during cache operations
 * - cachedCount — number of files currently cached
 */
import { useState, useCallback, useEffect, useRef } from "react";

export function useAudioCache() {
  const [isCaching, setIsCaching] = useState(false);
  const [cachedUrls, setCachedUrls] = useState<string[]>([]);
  const listenerRef = useRef<((e: MessageEvent) => void) | null>(null);

  // Listen for SW messages
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handler = (e: MessageEvent) => {
      if (e.data?.type === "CACHE_AUDIO_DONE") {
        setIsCaching(false);
        // Refresh cached URLs after caching
        navigator.serviceWorker.controller?.postMessage({ type: "GET_CACHED_URLS" });
      }
      if (e.data?.type === "AUDIO_CACHE_CLEARED") {
        setCachedUrls([]);
        setIsCaching(false);
      }
      if (e.data?.type === "CACHED_URLS") {
        setCachedUrls(e.data.urls || []);
      }
    };

    listenerRef.current = handler;
    navigator.serviceWorker.addEventListener("message", handler);

    // Initial fetch of cached URLs
    navigator.serviceWorker.ready.then(() => {
      navigator.serviceWorker.controller?.postMessage({ type: "GET_CACHED_URLS" });
    });

    return () => {
      if (listenerRef.current) {
        navigator.serviceWorker.removeEventListener("message", listenerRef.current);
      }
    };
  }, []);

  const cacheAudioFiles = useCallback((urls: string[]) => {
    if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) return;
    setIsCaching(true);
    navigator.serviceWorker.controller.postMessage({ type: "CACHE_AUDIO", urls });
  }, []);

  const clearCache = useCallback(() => {
    if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) return;
    setIsCaching(true);
    navigator.serviceWorker.controller.postMessage({ type: "CLEAR_AUDIO_CACHE" });
  }, []);

  const getCachedUrls = useCallback(() => {
    if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) return;
    navigator.serviceWorker.controller.postMessage({ type: "GET_CACHED_URLS" });
  }, []);

  const isUrlCached = useCallback(
    (url: string) => cachedUrls.some((u) => u === url || u.includes(url)),
    [cachedUrls]
  );

  return {
    cacheAudioFiles,
    clearCache,
    getCachedUrls,
    isUrlCached,
    isCaching,
    cachedUrls,
    cachedCount: cachedUrls.length,
  };
}
